import {store} from "../../Store/index.js";
import {AttachUserJWTToWebSocketConnection} from "../LibIntegrations/Apollo.js";
import {graph} from "../LibIntegrations/MobXGraphlink.js";
import {AddNotificationMessage, hourInMS, minuteInMS, RunInAction} from "web-vcore";
import {Timer} from "js-vextensions";

export const userJwtKey = "lucid-frontier-user-jwt";

type StandardJWTFields = {
	// header
	"alg": "HS256",
	"typ": "JWT"
	// payload
	"iat": number,
	"exp": number,
	"nbf": number,
};
// source struct is `UserInfoForJWT` in jwt_utils.rs
type CustomJWTFields = {
	"id": string,
	"email": string,
};

function ParseJWT(token: string) {
	// from: https://stackoverflow.com/a/38552302
	var base64Url = token.split(".")[1];
	var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

	try {
		// note: atob() will error if the string is not base64-encoded
		var jsonPayload = decodeURIComponent(window.atob(base64).split("").map(c=>{
			return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`;
		}).join(""));
		return JSON.parse(jsonPayload) as StandardJWTFields & CustomJWTFields;
	} catch {
		// in case the stored JWT string is invalid, simple cancel sign-in progression (rather than crash libs/app init procedure, due to)
		return null;
	}
}

export function GetUserInfoJWTString() {
	return localStorage.getItem(userJwtKey);
}
export function GetUserInfoFromStoredJWT() {
	// get the authentication token from local storage if it exists
	const token = GetUserInfoJWTString();
	if (token == null) return null;
	return ParseJWT(token);
}

export function OnUserJWTChanged() {
	//SendUserJWTToMGL();
	AttachUserJWTToWebSocketConnection();
}

// /** Call this once at startup (currently done at start of InitApollo()), and whenever the stored user-jwt changes. */
/** Call this whenever the jwt-data attached to the websocket connection is has changed. (so that requests are re-made, with the new auth-data) */
export async function SendUserJWTToMGL() {
	const userInfoFromToken = GetUserInfoFromStoredJWT();
	let promise: Promise<any>|n;
	RunInAction("SendUserJWTToMGL", ()=>{ // temp; unnecessary after mobx-graphlink release+update
		// maybe todo: if prior user-info matches the user-info we're about to set, then don't clear the cache (we may merely be calling this as part of websocket reconnect, thus it may not be necessary)
		if (userInfoFromToken != null) {
			promise = graph.SetUserInfo({id: userInfoFromToken.id}, !store.main.blockCacheClearing);
		} else {
			promise = graph.SetUserInfo(null, !store.main.blockCacheClearing);
		}
	});
	await promise;
}

// Once per minute, try to validate the token; if it's expired (or close to expiring), then delete the token and call RefreshUserInfoFromStoredJWT().
// This will cause the user to be logged out, making it visible to them that they then have to sign-in again. (which is needed for their commands to go through)
export const userInfoCheckTimer = new Timer(minuteInMS, ()=>{
	CheckIfUserInfoTokenExpired();
}).Start(0); // run once immediately, then once per minute

let timeOfLastSignInWarning = -1;
function CheckIfUserInfoTokenExpired() {
	const userInfoFromToken = GetUserInfoFromStoredJWT();
	if (userInfoFromToken == null) return;

	const now = Date.now();
	const timeOfTokenExpire = userInfoFromToken.exp * 1000; // convert to ms
	const timeOfTokenDestroy = timeOfTokenExpire - (10 * minuteInMS);
	const timeOfTokenWarn = timeOfTokenExpire - (24 * hourInMS);
	//const timeAtWhichTokenBecomesValid = userInfoFromToken.nbf * 1000; // convert to ms

	// if token is expired, or it's in the brief period where it's valid but ignored/treated-as-expired by client, delete the token and request new sign-in (the 10min buffer is in case clocks are slightly off)
	if (now >= timeOfTokenExpire || now >= timeOfTokenDestroy) {
		localStorage.removeItem(userJwtKey);
		OnUserJWTChanged();
		// we say it's "already expired" to be less confusing (even if token is merely "almost expired", users won't care to know that implementation detail)
		AddNotificationMessage(`Your sign-in information has expired. Please sign-in again.`);
	}
	// else, if token will expire soon (see timeOfTokenWarn above), simply warn the user (if we haven't already warned them in the last hour)
	else if (now >= timeOfTokenWarn && Date.now() - timeOfLastSignInWarning > hourInMS) {
		const hoursTillTokenDestroy = ((timeOfTokenDestroy - Date.now()) / hourInMS).RoundTo(1);
		// note: while we say it will "expire in X hours", technically it signifies that we'll destroy the token / sign them out at that time
		AddNotificationMessage(`Your sign-in information will expire in ${hoursTillTokenDestroy} hours. To avoid disruption, you can manually sign-out now then sign back in.`);
		timeOfLastSignInWarning = Date.now();
	}
}